import {
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Typography,
} from "@mui/material";
import type {
    KboSettings,
    KboSkillDefinition,
} from "components/KBo/Models/KboSettings";
import React, { useCallback, useMemo } from "react";

interface Props {
    selectedSkillNames?: string[] | null;
    availableSkills: KboSkillDefinition[];
    defaults?: Pick<KboSettings, "selectedSkillNames"> | null;
    onChange: (selectedSkillNames: string[]) => void;
}

export function KBoSkillToggles({
    selectedSkillNames,
    availableSkills,
    defaults,
    onChange,
}: Props) {
    const effectiveSelectedSkillNames = useMemo(
        () =>
            selectedSkillNames ??
            defaults?.selectedSkillNames ??
            [],
        [defaults?.selectedSkillNames, selectedSkillNames],
    );

    const selectedSkillSet = useMemo(
        () =>
            new Set(
                effectiveSelectedSkillNames.map((name) =>
                    name.toLocaleLowerCase(),
                ),
            ),
        [effectiveSelectedSkillNames],
    );

    const updateSkill = useCallback(
        (skillName: string, selected: boolean) => {
            const current = new Set(effectiveSelectedSkillNames);
            if (selected) {
                current.add(skillName);
            } else {
                current.delete(skillName);
            }

            const next = availableSkills
                .map((skill) => skill.name)
                .filter((name) => current.has(name));
            onChange(next);
        },
        [availableSkills, effectiveSelectedSkillNames, onChange],
    );

    return (
        <Grid item>
            <Paper>
                <Grid container direction="column" rowGap={2} padding={3}>
                    <Grid item>
                        <Typography variant="h6">
                            Skill Configuration
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Select the skills KBo can discover and load for
                            this configuration.
                        </Typography>
                        {availableSkills.length > 0 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {selectedSkillSet.size} of{" "}
                                {availableSkills.length} skills enabled
                            </Typography>
                        )}
                    </Grid>

                    {availableSkills.map((skill) => {
                        const checked = selectedSkillSet.has(
                            skill.name.toLocaleLowerCase(),
                        );

                        return (
                            <Grid
                                container
                                item
                                key={skill.name}
                                direction="column"
                                sx={{
                                    paddingInline: 1,
                                    paddingBlock: 0.5,
                                    borderRadius: 1,
                                    "&:hover": {
                                        backgroundColor: "action.hover",
                                    },
                                }}
                            >
                                <Grid
                                    container
                                    item
                                    alignItems="center"
                                    columnGap={2}
                                >
                                    <Grid item xs={4}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(_, isChecked) =>
                                                        updateSkill(
                                                            skill.name,
                                                            isChecked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    {skill.name}
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {skill.toolCount} mapped tool
                                            {skill.toolCount === 1 ? "" : "s"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item sx={{ pl: 5, pr: 2 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {skill.description}
                                    </Typography>
                                </Grid>
                            </Grid>
                        );
                    })}

                    {availableSkills.length === 0 && (
                        <Grid item>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                No skills available.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
}
